//#region    ////////// packages
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { cn } from '@/shared/shadcn/lib/utils';
//#endregion ////////// packages

//#region    ////////// components
import FloatingLabelInput from '@/shared/components/inputs/FloatingLabelInput';
import { UIButton } from '@/shared/components/button';
import AuthLayout from '@/pages/auth/_components/AuthLayout';
import LegalLinks from '@/shared/components/legal/LegalLinks';
//#endregion ////////// components

//#region    ////////// utils & hooks
import { useAuthStore } from '@/shared/auth/authStore';
import { purgeClientSession } from '@/shared/auth/purgeClientSession';
import { useExecMutationFetch } from '@/shared/apiClient/useExecMutationFetch';
import { useIsAppStyle } from '@/shared/hooks/useIsAppStyle';
import { getApiErrorMessage } from '@/shared/utils/queryUtils';
//#endregion ////////// utils & hooks

//#region    ////////// constants
import { PATH, HOME_PATH_BY_ROLE, USER_ROLE, USER_ROLE_KR } from '@/shared/constants';
//#endregion ////////// constants

import { authApi } from '@/pages/auth/_api';

import LogoGatePlusFull from '@/shared/assets/svg/logo/logo-gate_plus-full.svg?react';

type FormData = {
  loginId: string;
  password: string;
};

// 로그인은 태블릿 폭까지 모바일 뷰로 보여준다 (앱 셸 기준 767px보다 넓게)
const SIGNIN_MOBILE_MAX_WIDTH_PX = 1023;

// 개발용
const DEV_LOGIN_SHORTCUTS: Record<string, FormData> = {
  '1': { loginId: 'devaca@test.com', password: '1234567*' },
  '2': { loginId: 'inst@example.com', password: '12345678*' },
};

export default function SigninPage() {
  const navigate = useNavigate();
  const setUserInfo = useAuthStore((state) => state.setUserInfo);
  const isAppStyle = useIsAppStyle(SIGNIN_MOBILE_MAX_WIDTH_PX);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormData>();

  const { mutate: login } = useExecMutationFetch({
    mutationFn: async (data: FormData) => {
      if (data.loginId === data.password && DEV_LOGIN_SHORTCUTS[data.loginId]) {
        data = DEV_LOGIN_SHORTCUTS[data.loginId];
      }
      const loginRes = await authApi.login(data);

      if (!loginRes.success) throw new Error(loginRes.error?.message || '로그인에 실패했습니다.');

      if (loginRes.data.passwordUpdateRequired) {
        throw new Error('비밀번호 변경이 필요합니다. 이메일을 확인하여 임시 비밀번호로 재로그인 후 변경해주세요.');
      }

      if (!loginRes.data.accessToken) throw new Error('로그인에 실패했습니다.');
      window.localStorage.setItem('accessToken', loginRes.data.accessToken);

      const meRes = await authApi.getMe();
      if (!meRes.success) throw new Error(meRes.error?.message || '사용자 정보를 불러오지 못했습니다.');

      // 네이티브 앱은 student/parent 라우트만 번들에 있어 다른 역할로 로그인하면 갈 곳이 없다 — 앱에서만 막는다.
      // 모바일 웹은 같은 화면(뷰포트 기준)을 보여줘도 전체 라우트가 있는 웹이라 그대로 허용한다.
      if (Capacitor.isNativePlatform()) {
        const { userRole } = meRes.data;
        if (userRole !== USER_ROLE.STUDENT && userRole !== USER_ROLE.PARENT) {
          purgeClientSession();
          const roleLabel = userRole ? (USER_ROLE_KR[userRole as keyof typeof USER_ROLE_KR] ?? userRole) : '';
          throw new Error(`GATE+ ERP 앱은 학생·학부모 계정만 이용할 수 있습니다.${roleLabel ? ` (현재 계정: ${roleLabel})` : ''}`);
        }
      }

      return { ...meRes.data, loginId: data.loginId };
    },
    successHandler: (userInfo) => {
      setUserInfo(userInfo);
      navigate(HOME_PATH_BY_ROLE[userInfo.userRole] ?? PATH.ADMIN.HOME, { replace: true });
    },
    failHandler: (error) => {
      const message =
        error?.response?.status === 400 ? '이메일 또는 비밀번호를 확인해주세요.' : (getApiErrorMessage(error) ?? error?.message ?? '로그인에 실패했습니다.');
      setError('root', { message });
    },
  });

  const form = (
    <form onSubmit={handleSubmit((data) => login(data))} className={cn('flex flex-col', isAppStyle ? 'mt-6' : 'mt-3')} noValidate autoComplete='on'>
      <FloatingLabelInput
        {...register('loginId', { required: 'Email is required.', onChange: () => clearErrors('root') })}
        id='gate-erp-login-id'
        autoComplete='username'
        error={errors.loginId?.message}
        label='ID'
      />
      <FloatingLabelInput
        className='mt-2'
        type='password'
        {...register('password', { required: 'Password is required.', onChange: () => clearErrors('root') })}
        id='gate-erp-password'
        autoComplete='current-password'
        error={errors.password?.message}
        label='Password'
      />
      {errors.root?.message && <p className='text-state-error mt-3 text-sm'>{errors.root.message}</p>}
      <UIButton type='submit' size='large' shape='square' className='mt-8'>
        Sign in
      </UIButton>

      {import.meta.env.VITE_ENVIRONMENT === 'prod' && (
        <div className='border-table-line m-2 rounded-xl border p-2'>
          <p>테스트용 로그인 -</p>
          <div className='flex gap-2'>
            <UIButton type='button' size='small' shape='square' onClick={() => login({ loginId: 'admin', password: '12345678*' })}>
              관리자 로그인
            </UIButton>
          </div>
        </div>
      )}

      {import.meta.env.VITE_ENVIRONMENT === 'develop' && (
        <div className='border-table-line m-2 rounded-xl border p-2'>
          <p>개발 테스트용 로그인 -</p>
          <div className='flex gap-2'>
            <UIButton type='button' size='small' shape='square' onClick={() => login({ loginId: 'dusu01@test.com', password: '12345678*' })}>
              학생 로그인
            </UIButton>
            <UIButton type='button' size='small' shape='square' onClick={() => login({ loginId: 'dusup01@test.com', password: '12345678*' })}>
              학부모 로그인
            </UIButton>
            <UIButton type='button' size='small' shape='square' onClick={() => login({ loginId: 'admin', password: '12345678*' })}>
              관리자 로그인
            </UIButton>
            <UIButton type='button' size='small' shape='square' onClick={() => login({ loginId: 'a', password: '12345678*' })}>
              강사 로그인
            </UIButton>
          </div>
        </div>
      )}
    </form>
  );

  // 모바일(네이티브 앱 또는 좁은 뷰포트의 웹)은 capacitor-gate-erp에서 쓰던 폰 전용 중앙정렬 레이아웃을 그대로 쓰고,
  // PC 웹은 기존 AuthLayout(데스크탑 분할 패널)을 유지한다.
  return isAppStyle ? <AppStyleSignin form={form} /> : <DesktopSignin form={form} />;
}

function AppStyleSignin({ form }: { form: ReactNode }) {
  return (
    <div className='font-pretendard mx-auto flex min-h-full w-full max-w-100 flex-col justify-center px-6 py-12'>
      <div className='flex w-full justify-center'>
        <LogoGatePlusFull className='mb-10 h-auto w-11/12 text-blue-600' />
      </div>

      <h1 className='text-d2xl text-basic-black font-semibold'>Sign in</h1>
      <p className='mt-1 text-sm text-gray-900'>Get closer to your dream school with GATE+ ERP.</p>

      {form}

      <Link className='mt-8 self-center text-sm text-gray-900 underline' to={PATH.AUTH.RESET_PASSWORD}>
        Reset password
      </Link>

      <LegalLinks className='mt-10' />
    </div>
  );
}

function DesktopSignin({ form }: { form: ReactNode }) {
  return (
    <AuthLayout>
      <div>
        <div className='relative'>
          <h3 className={cn('text-basic-black text-2xl font-semibold whitespace-pre-line', 'md:text-d3xl')}>Sign in</h3>
        </div>
        <p className='mt-1 text-sm text-gray-900'>Get closer to your dream school with GATE+ ERP.</p>

        {form}

        <div className='mt-8'>
          <Link className='block text-center text-sm text-gray-900 underline hover:font-semibold' to={PATH.AUTH.RESET_PASSWORD}>
            Reset password
          </Link>
        </div>

        <LegalLinks className='mt-10' />
      </div>
    </AuthLayout>
  );
}
